import React, { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { AudienceCommentCard } from '@components/CommentCard/AudienceCommentCard';
import { getImageSize, getRow, getRowV2 } from '@components/Song/Audience/helpers';
import { Avatar } from '@components/basics/Images/Avatar';
import { HoverCard, StyledHoverContent } from '@components/main-components/hover-card';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType, TierNft } from '@graphql/reactQuery';
import { getFragment, TopCommentCardFragmentDoc } from '@graphql/reactQuery';
import { TopAudienceSeatFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import {
  SECOND_THIRD_ROW_DESKTOP_EMPTY_V2,
  SECOND_THIRD_ROW_DESKTOP_V2,
  SECOND_THIRD_ROW_MOBILE_EMPTY_V2,
  SECOND_THIRD_ROW_MOBILE_V2,
} from '../Audience/shared/constants';
import { EmptyRankCard, RankCard, showRank } from './RankCard';
import type { AudienceSortType } from './constants';

type Position = 'first' | 'mid' | 'last';

function Badge({ position, type }: { position: Position; type: 'star' | 'golden-egg' }) {
  const isStar = type === 'star';

  return (
    <div
      className={twMerge(
        position === 'first'
          ? 'h-5 w-5 md:h-7 md:w-7'
          : position === 'mid'
          ? 'h-4 w-4 md:h-6 md:w-6 lt:h-4 lt:w-4 dt:h-6 dt:w-6'
          : 'h-3 w-3 dt:h-5 dt:w-5',
      )}
    >
      <NextFutureImage
        alt={isStar ? 'starBadge' : 'goldenEggBadge'}
        src={isStar ? '/images/star-badge.png' : '/images/golden-egg-badge.png'}
        sizes="(max-width: 28px) 100%"
        style={{ objectFit: 'contain' }}
        fill
      />
    </div>
  );
}

export function AudienceSeatNew({
  seatFrag,
  index,
  tier,
  sort,
}: {
  seatFrag: FragmentType<TopAudienceSeatFragmentDoc>;
  index: number;
  tier: TierNft;
  sort: AudienceSortType;
}) {
  const seat = getFragment(TopAudienceSeatFragmentDoc, seatFrag);

  const triggerRef = useRef<HTMLAnchorElement>(null);

  const {
    ownedGoldenEgg,
    vipNftsCount,
    user: { avatar, username, publicAddress, webappUri },
  } = seat;

  const { tablet, tabletLandscape, laptop } = useMediaQuery();

  const midRowMax = tablet ? SECOND_THIRD_ROW_DESKTOP_V2 : SECOND_THIRD_ROW_MOBILE_V2;

  const position = getRowV2({
    index,
    midRowMax,
    mobileBreakpoint: !laptop,
  });

  const hasGoldenEgg = !!ownedGoldenEgg;
  // is VIP if has vipNftsCount and only GA filter is not selected
  const isVip = !!vipNftsCount && tier !== 'GA';

  const hasDecorators = hasGoldenEgg || isVip;

  const userSeat = (
    <div
      className={twMerge(
        'relative aspect-square h-auto w-full min-w-0 bg-neutral100',
        'md:rounded-lg [&_img]:w-full [&_img]:object-cover',
        position === 'first'
          ? 'col-span-6 rounded-lg'
          : position === 'mid'
          ? 'col-span-3 rounded-md'
          : 'col-span-2 rounded-[6px]',
        hasDecorators && 'text-gradient-animated',
        hasGoldenEgg ? 'golden-egg-border' : isVip ? 'limited-border' : '',
        hasDecorators && position !== 'mid' && 'p-1',
        hasDecorators && position === 'mid' && 'p-[3px] sm:p-1',
      )}
    >
      <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
        <div
          className={twMerge(
            'absolute z-above1',
            position === 'first'
              ? 'right-2 top-2'
              : position === 'mid'
              ? 'right-[5px] top-[5px] md:right-2 md:top-2'
              : 'right-[6px] top-[6px] dt:right-2 dt:top-2',
          )}
        >
          {hasDecorators && (
            <Badge position={position} type={hasGoldenEgg ? 'golden-egg' : 'star'} />
          )}
        </div>

        <Avatar
          size={getImageSize({ desktopBreakpoint: laptop, rowPos: position })}
          disableLink={!tabletLandscape}
          src={avatar?.url}
          username={username}
          walletAddress={publicAddress}
          borderRadius={8}
          webappUri={webappUri}
          priority={false}
        />

        {showRank({ rank: index }) && (
          <RankCard
            rankFrag={seat}
            rank={index + 1}
            type={hasGoldenEgg ? 'goldenEgg' : isVip ? 'vip' : 'normal'}
            tier={tier}
            sort={sort}
          />
        )}
      </AccessibleLink>
    </div>
  );

  if (!tablet) {
    return userSeat;
  }

  return (
    <HoverCard closeDelay={100}>
      <HoverCard.Trigger
        ref={triggerRef}
        className={twMerge(
          'min-w-0',
          hasDecorators && 'text-gradient-animated',
          hasGoldenEgg ? 'golden-egg-border' : isVip ? 'limited-border' : '',
          hasDecorators && position !== 'mid' && 'p-1',
          hasDecorators && position === 'mid' && 'p-[3px] sm:p-1',
        )}
        asChild
      >
        {userSeat}
      </HoverCard.Trigger>

      <StyledHoverContent
        className="min-w-[253px] p-0"
        sideOffset={8}
        side="top"
        onPointerDownOutside={event => {
          if (event.target === triggerRef.current) event.preventDefault();
        }}
      >
        <TopCommentCard
          data={seat}
          edition={hasGoldenEgg ? 'goldenEgg' : isVip ? 'vip' : 'ga'}
          tier={tier}
          sort={sort}
        />
      </StyledHoverContent>
    </HoverCard>
  );
}

export const TopCommentCard = ({
  data,
  edition,
  tier,
  sort,
}: {
  data: FragmentType<TopCommentCardFragmentDoc>;
  edition: 'vip' | 'ga' | 'goldenEgg';
  tier: TierNft;
  sort: AudienceSortType;
}) => {
  const {
    user: { username, publicAddress, artist, webappUri, avatar },
    nftsCount,
    gaNftsCount,
    vipNftsCount,
    mostRecentNftWithComment,
    firstNftCollectedDate,
  } = getFragment(TopCommentCardFragmentDoc, data);

  return (
    <AudienceCommentCard
      username={username}
      comment={mostRecentNftWithComment?.comment?.message}
      date={firstNftCollectedDate}
      publicAddress={publicAddress}
      totalEditions={tier === 'ALL' ? nftsCount : tier === 'VIP' ? vipNftsCount : gaNftsCount}
      avatarUrl={avatar?.url}
      isArtist={artist != null}
      webappUri={webappUri}
      edition={edition}
      sort={sort}
    />
  );
};

export const EmptySeat = ({
  index,
  tier,
  loading,
}: {
  index: number;
  tier: TierNft;
  loading: boolean;
}) => {
  const { tablet } = useMediaQuery();

  const midRowMax = tablet ? SECOND_THIRD_ROW_DESKTOP_EMPTY_V2 : SECOND_THIRD_ROW_MOBILE_EMPTY_V2;
  const isVip = tier !== 'GA' && !loading;

  const position = getRow({
    index,
    midRowMax,
  });

  return (
    <div
      key={`empty-seat-${index}`}
      className={twMerge(
        'relative aspect-square h-auto w-full rounded-lg bg-neutral100',
        'flex items-center justify-center [&_img]:w-full [&_img]:object-cover',
        isVip && 'limited-border text-gradient-animated p-1',
        position === 'first'
          ? 'col-span-6'
          : position === 'mid'
          ? 'col-span-3'
          : 'col-span-3 md:col-span-2',
        loading && 'animate-pulse',
      )}
    >
      <div className="absolute right-2 top-2 z-above1">
        {isVip && <Badge position={position} type="star" />}
      </div>
      {isVip && <div className="block h-full w-full rounded-lg bg-neutral100" />}
      {showRank({ rank: index }) && !loading && (
        <EmptyRankCard rank={index + 1} type={isVip ? 'vip' : 'normal'} />
      )}
    </div>
  );
};
